import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, first, map, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AccountSummary,
  ActivateAccountBody,
  ActivationPreview,
  BankReconciliationAccount,
  BankReconciliationActivation,
  BankReconciliationErrorBody,
  BulkMatchResponse,
  CandidatesQuery,
  CandidatesResponse,
  CreateAndMatchBody,
  PeriodQuery,
  CreateMatchBody,
  CreateMatchResponse,
  DiscardOperationResponse,
  DuplicateCheck,
  OperationResponse,
  RuleBody,
  RuleView,
  ResumeOperationBody,
  SettleAndMatchBody,
  TransactionView,
  UndoWithCancelResponse,
  DayDetail,
  DaysFilter,
  DaysResponse,
  IgnoreBody,
  ImportListResponse,
  ImportResult,
  LockResponse,
  LockStatus,
  LockStatusQuery,
  MatchDetail,
  MovementReconciliation,
  MovementStatus,
  PendingQuery,
  PendingResponse,
  TransactionSuggestionResponse,
  UndoBulkResponse,
  UndoMatchResponse,
  UnlockResponse,
  TitleReceiveInfo,
} from '../interface/bank-reconciliation';
import { DataService } from './data.service';

/** Código e corpo do erro de uma chamada da nova conciliação (`{ error, message }`). */
export function bankReconciliationErrorOf(error: unknown): { status: number; body: BankReconciliationErrorBody } {
  if (error instanceof HttpErrorResponse) {
    const body = (error.error && typeof error.error === 'object' ? error.error : {}) as Partial<BankReconciliationErrorBody>;
    return { status: error.status, body: { ...body, error: body.error ?? `HTTP_${error.status}` } };
  }
  return { status: 0, body: { error: 'UNKNOWN', message: error instanceof Error ? error.message : undefined } };
}

@Injectable({ providedIn: 'root' })
export class BankReconciliationService {
  private readonly _http = inject(HttpClient);
  private readonly _dataService = inject(DataService);
  private readonly _baseURL = `${environment.mkgoURL}/api/v1/bank-reconciliation`;

  /** `cnpj`: empresa explícita (telas fora do contexto de uma empresa, como o "editar empresa"). */
  private _options(params?: HttpParams, cnpj?: string) {
    return this._dataService.httpOptions(cnpj ?? false, params).pipe(first());
  }

  /** `multipart/form-data`: sem `Content-Type`, para o navegador definir o boundary. */
  private _multipartOptions() {
    return this._dataService.httpOptions(false).pipe(
      first(),
      map((options) => ({ headers: options.headers.delete('Content-Type') })),
    );
  }

  private static _params(values: Record<string, string | number | undefined | null>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    }
    return params;
  }

  listAccounts(cnpj?: string): Observable<{ accounts: BankReconciliationAccount[] }> {
    return this._options(undefined, cnpj).pipe(switchMap((options) => this._http.get<{ accounts: BankReconciliationAccount[] }>(`${this._baseURL}/accounts`, options)));
  }

  getActivationPreview(bankId: string, cutoffDate: string): Observable<ActivationPreview> {
    return this._options(BankReconciliationService._params({ cutoffDate })).pipe(
      switchMap((options) => this._http.get<ActivationPreview>(`${this._baseURL}/accounts/${bankId}/activation-preview`, options)),
    );
  }

  activateAccount(bankId: string, body: ActivateAccountBody): Observable<BankReconciliationActivation> {
    return this._options().pipe(
      switchMap((options) => this._http.post<BankReconciliationActivation>(`${this._baseURL}/accounts/${bankId}/activate`, body, options)),
    );
  }

  /** `from`/`to`, ou `all=1` para "Todo o período" (sem `from`/`to`). */
  private static _periodParams(period: PeriodQuery): Record<string, string | undefined> {
    return 'all' in period ? { all: '1' } : { from: period.from, to: period.to };
  }

  getSummary(bankId: string, period: PeriodQuery): Observable<AccountSummary> {
    return this._options(BankReconciliationService._params(BankReconciliationService._periodParams(period))).pipe(
      switchMap((options) => this._http.get<AccountSummary>(`${this._baseURL}/accounts/${bankId}/summary`, options)),
    );
  }

  getPending(bankId: string, query: PendingQuery): Observable<PendingResponse> {
    const params = BankReconciliationService._params({
      ...BankReconciliationService._periodParams(query),
      page: query.page,
      pageSize: query.pageSize,
      type: query.type,
      suggestion: query.suggestion,
      q: query.q?.trim(),
    });
    return this._options(params).pipe(
      switchMap((options) => this._http.get<PendingResponse>(`${this._baseURL}/accounts/${bankId}/pending`, options)),
    );
  }

  getTransactionSuggestion(transactionId: string): Observable<TransactionSuggestionResponse> {
    return this._options().pipe(
      switchMap((options) => this._http.get<TransactionSuggestionResponse>(`${this._baseURL}/transactions/${transactionId}/suggestion`, options)),
    );
  }

  searchCandidates(bankId: string, query: CandidatesQuery): Observable<CandidatesResponse> {
    const params = BankReconciliationService._params({
      transactionId: query.transactionId,
      q: query.q?.trim(),
      from: query.from,
      to: query.to,
      kinds: query.kinds?.join(','),
      limit: query.limit,
    });
    return this._options(params).pipe(
      switchMap((options) => this._http.get<CandidatesResponse>(`${this._baseURL}/accounts/${bankId}/candidates`, options)),
    );
  }

  importStatement(bankId: string, file: File, accountOverride = false): Observable<ImportResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (accountOverride) form.append('accountOverride', 'true');
    return this._multipartOptions().pipe(
      switchMap((options) => this._http.post<ImportResult>(`${this._baseURL}/accounts/${bankId}/imports`, form, options)),
    );
  }

  listImports(bankId: string, page = 1, limit = 20): Observable<ImportListResponse> {
    return this._options(BankReconciliationService._params({ page, limit })).pipe(
      switchMap((options) => this._http.get<ImportListResponse>(`${this._baseURL}/accounts/${bankId}/imports`, options)),
    );
  }

  createMatch(body: CreateMatchBody): Observable<CreateMatchResponse> {
    return this._options().pipe(switchMap((options) => this._http.post<CreateMatchResponse>(`${this._baseURL}/matches`, body, options)));
  }

  /** O lote recalcula as sugestões com o mesmo intervalo da lista (`from`/`to`). */
  applyBulk(transactionIds: string[], period?: PeriodQuery): Observable<BulkMatchResponse> {
    return this._options().pipe(
      switchMap((options) =>
        this._http.post<BulkMatchResponse>(`${this._baseURL}/matches/bulk`, { transactionIds, ...(period ? ('all' in period ? { all: true } : { from: period.from, to: period.to }) : {}) }, options),
      ),
    );
  }

  undoMatch(matchId: string): Observable<UndoMatchResponse> {
    return this._options().pipe(switchMap((options) => this._http.delete<UndoMatchResponse>(`${this._baseURL}/matches/${matchId}`, options)));
  }

  /** Desfaz e cancela as baixas e os títulos criados pela conciliação (`FINANCIAL.titles.cancelReceived`). */
  undoMatchCancellingCreated(matchId: string): Observable<UndoWithCancelResponse> {
    return this._options(BankReconciliationService._params({ cancelCreatedMovements: 'true' })).pipe(
      switchMap((options) => this._http.delete<UndoWithCancelResponse>(`${this._baseURL}/matches/${matchId}`, options)),
    );
  }

  // Saga (F3.1): o `202 OPERATION_IN_PROGRESS` chega como resposta de sucesso HTTP; `observe: 'response'` expõe o status.
  settleAndMatch(transactionId: string, body: SettleAndMatchBody | ResumeOperationBody): Observable<{ status: number; body: OperationResponse | { error?: string } }> {
    return this._options().pipe(
      switchMap((options) =>
        this._http.post<OperationResponse | { error?: string }>(`${this._baseURL}/transactions/${transactionId}/settle-and-match`, body, { ...options, observe: 'response' }),
      ),
      map((response) => ({ status: response.status, body: response.body as OperationResponse | { error?: string } })),
    );
  }

  createAndMatch(transactionId: string, body: CreateAndMatchBody | ResumeOperationBody): Observable<{ status: number; body: OperationResponse | { error?: string } }> {
    return this._options().pipe(
      switchMap((options) =>
        this._http.post<OperationResponse | { error?: string }>(`${this._baseURL}/transactions/${transactionId}/create-and-match`, body, { ...options, observe: 'response' }),
      ),
      map((response) => ({ status: response.status, body: response.body as OperationResponse | { error?: string } })),
    );
  }

  getTransaction(transactionId: string): Observable<TransactionView> {
    return this._options().pipe(switchMap((options) => this._http.get<TransactionView>(`${this._baseURL}/transactions/${transactionId}`, options)));
  }

  discardOperation(transactionId: string): Observable<DiscardOperationResponse> {
    return this._options().pipe(
      switchMap((options) => this._http.post<DiscardOperationResponse>(`${this._baseURL}/transactions/${transactionId}/discard-operation`, {}, options)),
    );
  }

  getDuplicateCheck(transactionId: string): Observable<DuplicateCheck> {
    return this._options().pipe(switchMap((options) => this._http.get<DuplicateCheck>(`${this._baseURL}/transactions/${transactionId}/duplicate-check`, options)));
  }

  // Regras de pré-preenchimento (`FINANCIAL.banks.conciliate`).
  listRules(): Observable<{ rules: RuleView[] }> {
    return this._options().pipe(switchMap((options) => this._http.get<{ rules: RuleView[] }>(`${this._baseURL}/rules`, options)));
  }

  createRule(body: RuleBody): Observable<RuleView> {
    return this._options().pipe(switchMap((options) => this._http.post<RuleView>(`${this._baseURL}/rules`, body, options)));
  }

  updateRule(ruleId: string, body: RuleBody): Observable<RuleView> {
    return this._options().pipe(switchMap((options) => this._http.patch<RuleView>(`${this._baseURL}/rules/${ruleId}`, body, options)));
  }

  deleteRule(ruleId: string): Observable<unknown> {
    return this._options().pipe(switchMap((options) => this._http.delete(`${this._baseURL}/rules/${ruleId}`, options)));
  }

  undoBulk(bulkId: string): Observable<UndoBulkResponse> {
    return this._options().pipe(switchMap((options) => this._http.delete<UndoBulkResponse>(`${this._baseURL}/matches/bulk/${bulkId}`, options)));
  }

  ignoreTransaction(transactionId: string, body: IgnoreBody): Observable<unknown> {
    return this._options().pipe(switchMap((options) => this._http.post(`${this._baseURL}/transactions/${transactionId}/ignore`, body, options)));
  }

  unignoreTransaction(transactionId: string): Observable<unknown> {
    return this._options().pipe(switchMap((options) => this._http.post(`${this._baseURL}/transactions/${transactionId}/unignore`, {}, options)));
  }

  /** Pré-checagem das telas legadas, antes de mexer em saldo. Fora do guard de flag: vale para toda empresa. */
  getLockStatus(query: LockStatusQuery): Observable<LockStatus> {
    return this._options(BankReconciliationService._params({ ...query })).pipe(
      switchMap((options) => this._http.get<LockStatus>(`${this._baseURL}/movements/lock-status`, options)),
    );
  }

  /** Coluna "Conciliada" da tela Bancos: vínculos ativos do período. Também fora do guard de flag. */
  getMovementStatus(bankId: string, from: string, to: string): Observable<MovementStatus> {
    return this._options(BankReconciliationService._params({ from, to })).pipe(
      switchMap((options) => this._http.get<MovementStatus>(`${this._baseURL}/accounts/${bankId}/movement-status`, options)),
    );
  }

  // ---------------------------------------------------------------------------
  // Movimentações por dia, detalhe e trava (F4)
  // ---------------------------------------------------------------------------
  getDays(bankId: string, period: PeriodQuery, filter: DaysFilter = 'all'): Observable<DaysResponse> {
    return this._options(BankReconciliationService._params({ ...BankReconciliationService._periodParams(period), filter })).pipe(
      switchMap((options) => this._http.get<DaysResponse>(`${this._baseURL}/accounts/${bankId}/days`, options)),
    );
  }

  getDayDetail(bankId: string, date: string): Observable<DayDetail> {
    return this._options().pipe(switchMap((options) => this._http.get<DayDetail>(`${this._baseURL}/accounts/${bankId}/days/${date}`, options)));
  }

  getMatch(matchId: string): Observable<MatchDetail> {
    return this._options().pipe(switchMap((options) => this._http.get<MatchDetail>(`${this._baseURL}/matches/${matchId}`, options)));
  }

  getMovementReconciliation(kind: 'receive' | 'event-item', id: string): Observable<MovementReconciliation> {
    return this._options().pipe(
      switchMap((options) => this._http.get<MovementReconciliation>(`${this._baseURL}/movements/${kind}/${id}/reconciliation`, options)),
    );
  }

  /**
   * Leitura simples do título (sem sessão de edição) para a seção "Informações da baixa" do detalhe: valor da parcela,
   * vencimento e os campos da baixa (`receiveId`).
   */
  getTitleReceiveInfo(titleId: string, receiveId: string): Observable<TitleReceiveInfo> {
    interface RawTitle {
      value?: number;
      expirationDate?: string;
      parcel?: number;
      received?: Array<{
        _id?: string;
        totalValue?: number;
        createDate?: string;
        condition?: string;
        description?: string;
        fees?: number;
        penaltyFee?: number;
        discount?: number;
        rate?: number;
        bank?: string;
      }>;
    }
    return this._options().pipe(
      switchMap((options) => this._http.get<RawTitle>(`${environment.mkgoURL}/api/v1/title/${titleId}`, options)),
      map((title) => {
        const receive = (title.received ?? []).find((item) => String(item._id) === receiveId);
        return {
          parcelValue: title.value ?? null,
          dueDate: title.expirationDate ?? null,
          parcel: title.parcel != null ? String(title.parcel) : null,
          receive: receive
            ? {
                totalValue: receive.totalValue ?? 0,
                createDate: receive.createDate ?? '',
                condition: receive.condition ?? null,
                description: receive.description ?? null,
                fees: receive.fees ?? 0,
                penaltyFee: receive.penaltyFee ?? 0,
                discount: receive.discount ?? 0,
                rate: receive.rate ?? 0,
                bank: receive.bank ?? null,
              }
            : null,
        };
      }),
    );
  }

  /** `through`: fim de mês já encerrado. Com pendências: `409 PERIOD_HAS_PENDING`. */
  lockPeriod(bankId: string, through: string): Observable<LockResponse> {
    return this._options().pipe(switchMap((options) => this._http.post<LockResponse>(`${this._baseURL}/accounts/${bankId}/lock`, { through }, options)));
  }

  /** `to`: fim de mês anterior à trava, ou `null` para reabrir tudo. */
  unlockPeriod(bankId: string, to: string | null): Observable<UnlockResponse> {
    return this._options().pipe(switchMap((options) => this._http.post<UnlockResponse>(`${this._baseURL}/accounts/${bankId}/unlock`, { to }, options)));
  }
}
